import React, { useState } from "react";
import PropTypes from "prop-types";
import MKBox from "./MKBox";
import MKButton from "./MKButton";
import MKInput from "./MKInput";
import { Typography, Modal, Stack, Alert, CircularProgress } from "@mui/material";
import API_BASE_URL from "../apiConfig";
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from "../constants";

function NewsItemModal({ open, onClose, clubId, clubName, onNewsCreated }) {
  const [form, setForm] = useState({
    title: "",
    content: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    
    if (!form.content.trim()) {
      setError("Content is required");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/news`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: form.title.trim(),
          content: form.content.trim()
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to create news item");
      }

      const newNewsItem = await res.json();
      setSuccess("News item created successfully!");
      
      // Reset form
      setForm({ title: "", content: "" });
      
      // Notify parent component
      if (onNewsCreated) {
        onNewsCreated(newNewsItem);
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1500);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setForm({ title: "", content: "" });
      setError("");
      setSuccess("");
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MKBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          maxWidth: "90vw",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflow: "auto",
        }}
      >
        <Typography variant="h5" fontWeight="bold" mb={2}>
          Create News Item for {clubName}
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <MKInput
              label="Title"
              name="title"
              value={form.title}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
            />
            
            <MKInput
              label="Content"
              name="content"
              value={form.content}
              onChange={handleChange}
              fullWidth
              multiline
              rows={6}
              required
              disabled={loading}
            />
            
            {success && <Alert severity="success">{success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            
            <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
              <MKButton
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </MKButton>
              <MKButton
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  minWidth: 120,
                  bgcolor: PRIMARY_PINK,
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: PRIMARY_PINK_DARK
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Creating...
                  </>
                ) : (
                  "Create News Item"
                )}
              </MKButton>
            </Stack>
          </Stack>
        </form>
      </MKBox>
    </Modal>
  );
}

NewsItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubId: PropTypes.string.isRequired,
  clubName: PropTypes.string.isRequired,
  onNewsCreated: PropTypes.func,
};

export default NewsItemModal; 